// ProfilePage — iOS HIG list-style settings
// Top: avatar + name + email
// Inset-grouped list of settings rows (KYC, wallet, payouts, help, log out)

import { useNavigate } from 'react-router-dom';
import { ErrorBox, Loading } from './UIShell';
import { useEffect, useState } from 'react';
import { getUser, clearAuth, getToken } from '../../lib/auth';
import { API_BASE } from '../../lib/auth';
import { APP_ID } from '@funderpro/shared';
import { UserAvatar, TierPill } from '../../components/Brand';

interface ProfileUser { id: string; email: string; full_name: string; kyc_status: string; balance: number; role: string; created_at: string }

export function ProfilePage() {
  const nav = useNavigate();
  const [me, setMe] = useState<ProfileUser | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const tok = getToken();
      if (!tok) return;
      try {
        const res = await fetch(`${API_BASE}/api/apps/${APP_ID}/auth/me`, { headers: { Authorization: `Bearer ${tok}` } });
        if (!res.ok) throw new Error('Failed to load');
        setMe(await res.json());
      } catch (e: any) { setErr(e?.message); }
    })();
  }, []);

  if (err) return <div className="p-4"><ErrorBox>{err}</ErrorBox></div>;
  if (!me) return <Loading label="Loading profile…" />;

  const initials = (me.full_name || me.email).split(/\s+/).map((s: string) => s[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="p-4">
      <h1 className="px-1 text-[34px] font-bold leading-[1.1] tracking-tight text-tf-text-1">Profile</h1>

      {/* Avatar + name */}
      <div className="mt-4 flex items-center gap-3 rounded-[16px] bg-white p-4">
        <UserAvatar size={56} name={me.full_name || me.email} />
        <div className="flex-1 min-w-0">
          <div className="truncate text-[17px] font-semibold text-tf-text-1">{me.full_name || 'Trader'}</div>
          <div className="truncate text-[13px] text-tf-text-3">{me.email}</div>
        </div>
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>

      {/* Account section */}
      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Account</h2>
      <div className="overflow-hidden rounded-[16px] bg-white">
        <Row icon={<WalletIcon />} label="Wallet" value={`$${me.balance.toFixed(2)}`} onClick={() => nav('/portal/wallet/deposit')} testId="profile-wallet" />
        <Row icon={<CardIcon />} label="Subscription" onClick={() => nav('/portal/subscriptions')} testId="profile-subs" />
        <Row icon={<ShieldIcon />} label="KYC" value={me.kyc_status === 'verified' ? 'Verified' : me.kyc_status === 'pending' ? 'Pending' : 'Not started'} onClick={() => nav('/portal/kyc')} testId="profile-kyc" last />
      </div>

      {/* Activity section */}
      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Activity</h2>
      <div className="overflow-hidden rounded-[16px] bg-white">
        <Row icon={<OrdersIcon />} label="Orders" onClick={() => nav('/portal/orders')} testId="profile-orders" />
        <Row icon={<PayoutIcon />} label="Payouts" onClick={() => nav('/portal/payouts')} testId="profile-payouts" />
        <Row icon={<ChallengeIcon />} label="My challenges" onClick={() => nav('/portal/dashboard')} testId="profile-challenges" last />
      </div>

      {/* Trading section */}
      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Trading</h2>
      <div className="overflow-hidden rounded-[16px] bg-white">
        <Row icon={<MarketsIcon />} label="Markets" onClick={() => nav('/portal/markets')} testId="profile-markets" />
        <Row icon={<PlatformsIcon />} label="Platforms" onClick={() => nav('/portal/platforms')} testId="profile-platforms" />
        <Row icon={<CalIcon />} label="Economic calendar" onClick={() => nav('/portal/economic-calendar')} testId="profile-cal" />
        <Row icon={<AffIcon />} label="Affiliate" value="Earn" onClick={() => nav('/portal/affiliate')} testId="profile-affiliate" last />
      </div>

      {/* Support section */}
      <h2 className="mt-6 px-1 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Support</h2>
      <div className="overflow-hidden rounded-[16px] bg-white">
        <Row icon={<HelpIcon />} label="Help & FAQ" onClick={() => nav('/portal/help')} testId="profile-help" />
        <Row icon={<LogOutIcon />} label="Log out" onClick={() => { clearAuth(); window.location.href = '/login'; }} testId="profile-logout" last />
      </div>

      <p className="mt-6 text-center text-[11px] text-tf-text-muted">Funder Pro v1.0 · Build 2026.07</p>
    </div>
  );
}

function Row({ icon, label, value, onClick, testId, last }: { icon: React.ReactNode; label: string; value?: string; onClick?: () => void; testId?: string; last?: boolean }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={['flex w-full items-center gap-3 px-4 py-3 text-left active:bg-[#E5E5EA]/60', last ? '' : 'border-b border-tf-border-strong/55'].join(' ')}
    >
      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-tf-blue/10 text-tf-blue">{icon}</div>
      <span className="flex-1 text-[15px] text-tf-text-1">{label}</span>
      {value && <span className="text-[15px] text-tf-text-3">{value}</span>}
      <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2"><path d="m9 6 6 6-6 6" strokeLinecap="round" strokeLinejoin="round" /></svg>
    </button>
  );
}

function WalletIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="6" width="20" height="14" rx="2" /><path d="M16 13h2" strokeLinecap="round" /></svg>; }
function CardIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>; }
function ShieldIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2 4 5v7c0 5 4 9 8 10 4-1 8-5 8-10V5l-8-3z" /></svg>; }
function OrdersIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2" /></svg>; }
function PayoutIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18M7 15h4" strokeLinecap="round" /></svg>; }
function ChallengeIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6" /><path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5" /></svg>; }
function MarketsIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></svg>; }
function PlatformsIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M8 21h8M12 18v3" /></svg>; }
function CalIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 9h18M8 3v4M16 3v4" /></svg>; }
function AffIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 12V8a2 2 0 0 0-2-2h-2M4 12v4a2 2 0 0 0 2 2h2M12 15V9M16 5l-4 4-4-4" strokeLinecap="round" strokeLinejoin="round" /></svg>; }
function HelpIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M9 9a3 3 0 1 1 5 2c-1 1-2 1-2 3M12 17h.01" strokeLinecap="round" /></svg>; }
function LogOutIcon() { return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" strokeLinecap="round" strokeLinejoin="round" /></svg>; }
